import { useId, useState } from "react";
import { Link, useNavigate } from "react-router";
import { inputsCsv, resultCsv, type CalcResult, type Scenario } from "@h2lca/core";
import { Button } from "@/components/ui/FormControls";
import { InfoTip } from "@/components/ui/InfoTip";
import { Menu } from "@/components/ui/Menu";
import { useWorkshop } from "@/store/workshop";
import { MethodBadge } from "./Badges";
import { MethodCriteria } from "./MethodCriteria";
import { SAVE_HELP, downloadText, safeFileName, saveState } from "./helpers";
import { useSave } from "./useSave";

/**
 * 산정 화면 머리 , 경로(산정 시작 › 이름), 산정 방식, 저장 상태, 동작(저장,내보내기,더 보기).
 * 이름은 줄이지 않는다. 저장 상태는 어디에 저장됐는지까지 말한다.
 */
export function WorkspaceHeader({ scenario, result }: { scenario: Scenario; result: CalcResult }) {
  const nav = useNavigate();
  const [criteriaOpen, setCriteriaOpen] = useState(false);
  const criteriaId = useId();
  const criteriaTriggerId = useId();
  const serverId = useWorkshop((s) => s.serverId);
  const dirty = useWorkshop((s) => s.dirty);
  const lastSavedAt = useWorkshop((s) => s.lastSavedAt);
  const exportFile = useWorkshop((s) => s.exportFile);
  const reset = useWorkshop((s) => s.reset);
  const { save, saving, error } = useSave(result);
  const state = saveState(serverId, dirty, lastSavedAt);
  const base = safeFileName(scenario.name);

  return (
    <header className="page-header">
      <div className="min-w-0">
        <p className="text-caption text-ink-subtle">
          <Link to="/app" className="hover:text-accent">
            산정 시작
          </Link>
          <span className="mx-inline">›</span>
          <span>{scenario.name}</span>
        </p>
        <div className="mt-label flex flex-wrap items-center gap-x-content gap-y-label">
          <h1>{scenario.name}</h1>
          <div className="flex flex-wrap items-center gap-item">
            <MethodBadge method={scenario.method} />
            <Button
              id={criteriaTriggerId}
              variant="secondary"
              aria-expanded={criteriaOpen}
              aria-controls={criteriaId}
              onClick={() => setCriteriaOpen((open) => !open)}
            >
              {criteriaOpen ? "적용 기준 접기" : "적용 기준 보기"}
            </Button>
          </div>
        </div>
        <p className={`mt-label flex items-center gap-label text-caption ${error ? "text-tier-tight" : state.tone === "ok" ? "text-tier-ample" : "text-tier-moderate"}`}>
          {error ?? state.text}
          <InfoTip label="저장 방식 안내">{SAVE_HELP}</InfoTip>
        </p>
      </div>
      <div className="page-header-actions">
        <Menu
          label="내보내기"
          trigger={<span>내보내기</span>}
          items={[
            { label: "시나리오 파일 (.h2lca.json)", hint: "설비 구성과 입력값. 다시 불러올 수 있습니다", onSelect: exportFile },
            { label: "입력값 CSV", hint: "설비, 활동자료, 배출계수, 설비별 배출량", onSelect: () => downloadText(`${base}_입력값.csv`, inputsCsv(scenario, result), "text/csv;charset=utf-8") },
            { label: "결과 CSV", hint: "산정기준, 수소량, 총량, 단계별, 설비별", onSelect: () => downloadText(`${base}_결과.csv`, resultCsv(scenario, result), "text/csv;charset=utf-8") },
            { label: "결과보고서", hint: "보고서 확인 및 PDF 저장", to: "/app/report" },
          ]}
        />
        <Menu
          label="더 보기"
          trigger={<span>더 보기</span>}
          items={[
            {
              label: "시나리오 닫기(지우기)",
              hint: "보관함에 저장하지 않은 내용은 사라집니다",
              danger: true,
              onSelect: () => {
                if (window.confirm("이 시나리오를 닫고 목록으로 돌아갈까요? 보관함에 저장하지 않은 내용은 사라집니다.")) {
                  reset();
                  nav("/app");
                }
              },
            },
          ]}
        />
        {(state.tone !== "ok" || error) && (
          <Button variant="primary" onClick={() => void save()} disabled={saving}>
            {saving ? "저장 중…" : "보관함에 저장"}
          </Button>
        )}
      </div>
      <div id={criteriaId} role="region" aria-labelledby={criteriaTriggerId} className="min-w-0 w-full" hidden={!criteriaOpen}>
        {criteriaOpen && <MethodCriteria method={scenario.method} />}
      </div>
    </header>
  );
}
